import { useState, useEffect, useCallback, useRef } from "preact/hooks";

const DEFAULT_INTERVAL_MS = 30_000;

export type UseDataResult<T> = {
  data: T | null;
  loading: boolean;
  error: Error | null;
  lastUpdated: Date | null;
  refresh: () => Promise<void>;
};

export function useData<T>(
  fetchFn: () => Promise<T>,
  interval = DEFAULT_INTERVAL_MS
): UseDataResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const intervalRef = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await fetchFn();
      setData(result);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [fetchFn]);

  useEffect(() => {
    refresh();

    const startInterval = () => {
      intervalRef.current = window.setInterval(() => {
        if (document.visibilityState === "visible") {
          refresh();
        }
      }, interval);
    };

    startInterval();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refresh();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refresh, interval]);

  return { data, loading, error, lastUpdated, refresh };
}
